import { EventEntityType } from '@atlas/types'
import { CardShell, Field, PaginationFooter, SectionCard, SelectField } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { useAuditEvents } from '@/hooks/useAuditEvents'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { AuditTable } from './AuditTable'

/**
 * The Administration audit log: every audited event, across every entity, newest
 * first.
 *
 * Exists because the event log was only readable one entity at a time - the
 * work-order timeline, or the dashboard's newest-N feed - so "what happened to
 * this file / rule / classification?" was answered with a `psql` session. Follows
 * `WebhookDiagnostics`: same cards, same table, same real-total pagination.
 */

/** Page size for this surface. Sent explicitly on every request, so it need not match `GET /events`'s own default. */
const PAGE_SIZE = 25

/** A pasted UUID would otherwise fire one request per character. */
const ENTITY_ID_DEBOUNCE_MS = 250

const ENTITY_TYPE_OPTIONS = Object.values(EventEntityType).map((value) => ({ value, label: value }))

function AuditFilterBar({
	entityType,
	entityIdInput,
	isFiltered,
	onEntityType,
	onEntityIdInput,
	onClear,
}: {
	entityType: EventEntityType | ''
	entityIdInput: string
	isFiltered: boolean
	onEntityType: (value: EventEntityType | '') => void
	onEntityIdInput: (value: string) => void
	onClear: () => void
}) {
	return (
		<div className="flex flex-wrap items-end gap-4">
			<div className="w-64">
				<SelectField
					label="Entity type"
					value={entityType}
					options={ENTITY_TYPE_OPTIONS}
					placeholder="any entity type"
					onChange={(value) => onEntityType(value as EventEntityType | '')}
				/>
			</div>
			<div className="w-96">
				<Field
					label="Entity id"
					value={entityIdInput}
					placeholder="paste an id to see only its events"
					onChange={(event) => onEntityIdInput(event.target.value)}
				/>
			</div>
			<Button variant="outline" isDisabled={!isFiltered} onPress={onClear}>
				Clear filters
			</Button>
		</div>
	)
}

export function AuditLogPage() {
	const [entityType, setEntityType] = useState<EventEntityType | ''>('')
	const [entityIdInput, setEntityIdInput] = useState('')
	const [page, setPage] = useState(1)
	const [expandedId, setExpandedId] = useState<string | null>(null)

	const trimmedEntityId = entityIdInput.trim()
	const debouncedEntityId = useDebouncedValue(trimmedEntityId, ENTITY_ID_DEBOUNCE_MS)
	// An emptied field takes effect at once. The debounce exists to stop a pasted
	// UUID firing a request per character; clearing the filter is a single
	// deliberate act, and waiting on the timer would leave the table showing a
	// filtered result under a filter bar that already reads as cleared.
	const entityId = trimmedEntityId === '' ? '' : debouncedEntityId

	const query = useAuditEvents({
		entityType: entityType || undefined,
		entityId: entityId || undefined,
		page,
		limit: PAGE_SIZE,
	})

	// Every filter change resets to page 1, otherwise a narrower filter strands the
	// user on an empty page 7 of a two-page result. The debounced entity id changes
	// without a handler to hang this on, so it resets in an effect below.
	const applyFilter = (change: () => void) => {
		change()
		setPage(1)
		setExpandedId(null)
	}

	useEffect(() => {
		setPage(1)
		setExpandedId(null)
	}, [entityId])

	const pagination = query.data?.pagination
	const totalPages = pagination?.totalPages ?? 1
	const total = pagination?.total ?? 0

	// A refetch can shrink the result under a high page - entries do not disappear,
	// but a narrowed filter's first response arrives while the old page is still
	// selected - leaving "page 4 of 2" over an empty table.
	useEffect(() => {
		if (page > totalPages) setPage(totalPages)
	}, [page, totalPages])

	const isFiltered = entityType !== '' || entityIdInput !== ''

	return (
		<div className="space-y-6">
			<div className="flex items-start justify-between">
				<div>
					<h1 className="font-semibold text-2xl">Audit log</h1>
					<p className="text-ds-text-subtle">Every recorded event, across every entity.</p>
				</div>
				<Button variant="outline" onPress={() => void query.refetch()} isDisabled={query.isFetching}>
					{query.isFetching ? 'Refreshing…' : 'Refresh'}
				</Button>
			</div>

			<SectionCard
				title="Filters"
				description={
					<>
						Showing {total} matching {total === 1 ? 'entry' : 'entries'}.
					</>
				}
			>
				<AuditFilterBar
					entityType={entityType}
					entityIdInput={entityIdInput}
					isFiltered={isFiltered}
					onEntityType={(value) => applyFilter(() => setEntityType(value))}
					onEntityIdInput={setEntityIdInput}
					onClear={() =>
						applyFilter(() => {
							setEntityType('')
							setEntityIdInput('')
						})
					}
				/>
			</SectionCard>

			<CardShell>
				<AuditTable
					rows={query.data?.data ?? []}
					isLoading={query.isLoading}
					isError={query.isError}
					expandedId={expandedId}
					onToggleExpanded={(id) => setExpandedId((current) => (current === id ? null : id))}
				/>

				<PaginationFooter
					noun="entry"
					nounPlural="entries"
					page={page}
					totalPages={totalPages}
					total={total}
					isFetching={query.isFetching}
					onPage={(next) => {
						setExpandedId(null)
						setPage(Math.min(Math.max(1, next), totalPages))
					}}
				/>
			</CardShell>
		</div>
	)
}
